import type {
  ProjectCreateRequest,
  ProjectResponse,
  ProjectUpdateRequest,
  ProjectStatsResponse,
  MerchantOverviewResponse,
  StatsPeriod,
} from '../types';
import type { RequestFn } from '../client';

/** Create and manage projects — optional grouping layers that contain one or more shops. */
export class Projects {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create a new project under a merchant account.
   *
   * @param params - Project creation parameters (name, description, etc.).
   * @param merchantId - The merchant account ID that owns this project.
   * @returns The created project.
   *
   * @example
   * ```typescript
   * const project = await delopay.projects.create({ name: 'EU Stores' }, 'merch_123');
   * ```
   */
  async create(params: ProjectCreateRequest, merchantId: string): Promise<ProjectResponse> {
    return this.request('POST', '/projects', {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Retrieve a project by its ID.
   *
   * @param projectId - The unique project ID.
   * @param merchantId - Optional merchant scope. When provided, sent as
   *   `?merchant_id=…` — required by dashboards that authenticate with a JWT
   *   spanning multiple merchants and need to disambiguate which one this
   *   call applies to. API-key callers can omit it.
   * @returns The project.
   */
  async retrieve(projectId: string, merchantId?: string): Promise<ProjectResponse> {
    const path = `/projects/${encodeURIComponent(projectId)}`;
    if (merchantId === undefined) return this.request('GET', path);
    return this.request('GET', path, { query: { merchant_id: merchantId } });
  }

  /**
   * Update a project's details.
   *
   * @param projectId - The project ID to update.
   * @param params - Fields to update.
   * @param merchantId - Optional merchant scope. See {@link Projects.retrieve}.
   * @returns The updated project.
   */
  async update(
    projectId: string,
    params: ProjectUpdateRequest,
    merchantId?: string,
  ): Promise<ProjectResponse> {
    const path = `/projects/${encodeURIComponent(projectId)}`;
    if (merchantId === undefined) return this.request('PUT', path, { body: params });
    return this.request('PUT', path, { body: params, query: { merchant_id: merchantId } });
  }

  /**
   * Delete a project.
   *
   * @param projectId - The project ID to delete.
   * @param merchantId - Optional merchant scope. See {@link Projects.retrieve}.
   * @returns The deleted project object.
   */
  async delete(projectId: string, merchantId?: string): Promise<ProjectResponse> {
    const path = `/projects/${encodeURIComponent(projectId)}`;
    if (merchantId === undefined) return this.request('DELETE', path);
    return this.request('DELETE', path, { query: { merchant_id: merchantId } });
  }

  /**
   * List all projects for a merchant.
   *
   * @param merchantId - The merchant account ID.
   * @returns Array of projects.
   */
  async list(merchantId: string): Promise<ProjectResponse[]> {
    return this.request('GET', '/projects/list', {
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Get aggregate payment statistics across all projects for a merchant.
   *
   * The response's flat `shops[]` array holds every shop, including shops that
   * belong to no project — those are absent from `projects[].shops[]`, so look
   * a single shop up in `shops[]`. Requires `MerchantAccountRead`; a
   * shop-scoped user should call {@link Shops.stats} instead.
   *
   * @param merchantId - The merchant account ID.
   * @param period - Window in days, or `'all'` for an all-time total.
   *   Omitted means the server default of 30 days.
   * @returns Project statistics.
   *
   * @example
   * ```typescript
   * const stats = await delopay.projects.stats('merch_123', 'all');
   * const shop = stats.shops.find((s) => s.shop_id === 'pro_1');
   * ```
   */
  async stats(merchantId: string, period?: StatsPeriod): Promise<ProjectStatsResponse> {
    const query: Record<string, string> = { merchant_id: merchantId };
    if (period !== undefined) query['period'] = String(period);
    return this.request('GET', '/projects/stats', { query });
  }

  /**
   * Get a high-level overview (volume, counts, top connectors) for a merchant.
   *
   * @param merchantId - The merchant account ID.
   * @returns Merchant overview data.
   */
  async overview(merchantId: string): Promise<MerchantOverviewResponse> {
    return this.request('GET', '/projects/overview', {
      query: { merchant_id: merchantId },
    });
  }
}
